const ACTIONS_FIELD = 'boxActions';
const ACTIONS_KEY = '_actions';
/* Mise en forme du texte en cours d'édition (RichTextSidebarSection) : une
   section à part, juste sous les actions, invisible tant qu'aucun texte du
   bloc n'a le curseur. */
const TEXT_FIELD = 'textFormatting';
const TEXT_KEY = '_text';
const VISIBILITY_KEY = 'isVisible';

/* Tuiles de la palette (presse-papiers) : jamais sélectionnées en page. */
const isPaletteTile = (name) => name.startsWith('Clipboard');

/* Puck appelle `resolveFields` à chaque changement de l'item : le même jeu de
   champs doit rendre le même objet, sinon l'inspecteur se reconstruirait. */
const actionsFirstCache = new WeakMap();

/**
 * `_actions` en tête. La visibilité quitte la liste des champs pour devenir
 * une tuile de la rangée d'actions : le champ Actions sait qu'il doit la
 * montrer par `withVisibility`.
 */
const withActionsFirst = (fields) => {
  if (!fields) return fields;
  const cached = actionsFirstCache.get(fields);
  if (cached) return cached;
  const {
    [ACTIONS_KEY]: _, [TEXT_KEY]: __, [VISIBILITY_KEY]: visibility, ...rest
  } = fields;
  const next = {
    [ACTIONS_KEY]: { type: ACTIONS_FIELD, ...(visibility ? { withVisibility: true } : {}) },
    [TEXT_KEY]: { type: TEXT_FIELD },
    ...rest,
  };
  actionsFirstCache.set(fields, next);
  actionsFirstCache.set(next, next);
  return next;
};

/**
 * Pose la section « Actions » en tête de l'inspecteur de chaque bloc (Copier,
 * Supprimer…), à la place de la barre flottante de Puck. Le champ n'écrit
 * aucune prop (cf. puckBoxActionsField), sauf la visibilité qu'il reprend.
 *
 * `resolveFields` repart des champs qu'on lui passe, mais rien ne garantit
 * qu'il les conserve tous : on remet `_actions` en tête de son résultat. Il
 * reçoit les champs d'origine, visibilité comprise : c'est lui qui décide si
 * le bloc en a une (un Bloc dans un onglet n'en a pas, cf. nestedSectionFields).
 */
export function withBoxActions(components) {
  return Object.fromEntries(
    Object.entries(components).map(([name, def]) => {
      if (!def || isPaletteTile(name)) return [name, def];
      const next = { ...def, fields: withActionsFirst(def.fields) };
      if (typeof def.resolveFields === 'function') {
        next.resolveFields = (data, params, ...rest) => {
          const fields = params?.fields === next.fields ? def.fields : params?.fields;
          const resolved = def.resolveFields(data, { ...params, fields }, ...rest);
          return typeof resolved?.then === 'function'
            ? resolved.then(withActionsFirst)
            : withActionsFirst(resolved);
        };
      }
      return [name, next];
    }),
  );
}
